import React, { useState } from "react";
import {
    Box,
    Grid,
    Paper,
    Stack,
    Typography,
} from "@mui/material";

import * as XLSX from "xlsx";

import Button from "../../../components/Common/Button";
import AutoCompleteServerSide from "../../../components/Common/AutoCompleteServerSide";
import AttendenceReportsTable from "../../../components/admin/attendence-reports/AttendenceReportsTable";

const AttendenceReportsContainer = (props) => {
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleActivityChange = (value) => {
        setSelectedActivity(value);
        setSelectedBatch(null);
        setMembers([]);
    };

    const handleFindMembers = async () => {
        if (!selectedActivity || !selectedBatch) {
            return;
        }

        try {
            setLoading(true);

            const response = await props.getBookingList({
                pageNo: 0,
                limit: 1000,
                sortBy: -1,
                sortField: "createdAt",
                active: true,
                type: "enrollment",

                // IMPORTANT:
                // Use MongoDB _id values
                activity_id: selectedActivity._id,
                batch: selectedBatch._id,
            });

            console.log("Attendance API response:", response);

            const enrolledMembers = (response?.data?.result || []).filter(
                (item) => item?.payment_status === "Success"
            );

            setMembers(enrolledMembers);

        } catch (error) {
            console.error(
                "Failed to fetch enrolled students:",
                error
            );
            setMembers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateExcel = () => {
        if (!members || members.length === 0) {
            alert("Please find enrolled students first.");
            return;
        }

        const workbook = XLSX.utils.book_new();

        // Current month
        const currentDate = new Date();
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        // Number of days in current month
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        // --------------------------------------------------
        // Get batch information
        // --------------------------------------------------

        const batch =
            Array.isArray(selectedBatch?.batch_data)
                ? selectedBatch.batch_data[0]
                : selectedBatch;

        const batchName =
            batch?.batch_name ||
            selectedBatch?.batch_name ||
            "";

        // Format time exactly like:
        // 06:00 to 06:55 PM
        const formatTime = (dateValue) => {
            if (!dateValue) return "";

            const date = new Date(dateValue);

            return date.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            });
        };

        const startTime = formatTime(batch?.start_time);
        const endTime = formatTime(batch?.end_time);

        const timeRange =
            startTime && endTime
                ? `${startTime} to ${endTime}`
                : "";

        // --------------------------------------------------
        // Create worksheet data
        // --------------------------------------------------

        const totalColumns = 2 + daysInMonth;

        const headerRow = new Array(totalColumns).fill("");

        // A1
        headerRow[0] = "srno";

        // B1
        headerRow[1] = timeRange;

        // C1 onward
        headerRow[2] = batchName;

        const rows = [headerRow];

        // --------------------------------------------------
        // Sort students by participant name A → Z
        // --------------------------------------------------

        const sortedMembers = [...members].sort((a, b) => {
            const secondaryA =
                Array.isArray(a?.family_member)
                    ? a.family_member.find((member) => member)
                    : null;

            const secondaryB =
                Array.isArray(b?.family_member)
                    ? b.family_member.find((member) => member)
                    : null;

            const nameA =
                secondaryA?.name ||
                a?.member_id?.name ||
                "";

            const nameB =
                secondaryB?.name ||
                b?.member_id?.name ||
                "";

            return nameA.localeCompare(nameB, undefined, {
                sensitivity: "base",
            });
        });

        // --------------------------------------------------
        // Student rows
        // --------------------------------------------------

        sortedMembers.forEach((row, index) => {
            const primaryMember = row?.member_id || {};

            const secondaryMember =
                Array.isArray(row?.family_member)
                    ? row.family_member.find((member) => member)
                    : null;

            const participant = secondaryMember || primaryMember;

            const studentRow = new Array(totalColumns).fill("");

            // Sr No
            studentRow[0] = index + 1;

            // Student Name
            studentRow[1] =
                participant?.name ||
                primaryMember?.name ||
                "";

            // C onward remain blank for attendance
            rows.push(studentRow);
        });

        const worksheet = XLSX.utils.aoa_to_sheet(rows);

        // --------------------------------------------------
        // Merge batch name across all date columns
        // --------------------------------------------------

        worksheet["!merges"] = [
            {
                s: { r: 0, c: 2 },
                e: { r: 0, c: totalColumns - 1 },
            },
        ];

        // --------------------------------------------------
        // Column widths
        // --------------------------------------------------

        worksheet["!cols"] = [
            { wch: 8 },   // Sr No
            { wch: 24 },  // Time / Name
        ];

        for (let i = 0; i < daysInMonth; i++) {
            worksheet["!cols"].push({
                wch: 5,
            });
        }

        // --------------------------------------------------
        // Row heights
        // --------------------------------------------------

        worksheet["!rows"] = [
            { hpt: 25 },
        ];

        // --------------------------------------------------
        // Borders + alignment
        // --------------------------------------------------

        const range = XLSX.utils.decode_range(worksheet["!ref"]);

        for (let row = range.s.r; row <= range.e.r; row++) {
            for (let col = range.s.c; col <= range.e.c; col++) {
                const cellAddress = XLSX.utils.encode_cell({
                    r: row,
                    c: col,
                });

                if (!worksheet[cellAddress]) {
                    worksheet[cellAddress] = {
                        v: "",
                        t: "s",
                    };
                }

                worksheet[cellAddress].s = {
                    alignment: {
                        horizontal: "center",
                        vertical: "center",
                    },
                    border: {
                        top: {
                            style: "thin",
                        },
                        bottom: {
                            style: "thin",
                        },
                        left: {
                            style: "thin",
                        },
                        right: {
                            style: "thin",
                        },
                    },
                };
            }
        }

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Attendance"
        );

        const safeActivityName =
            selectedActivity?.name
                ?.replace(/[^a-zA-Z0-9-_ ]/g, "") ||
            "Activity";

        const safeBatchName =
            batchName.replace(/[^a-zA-Z0-9-_ ]/g, "") ||
            "Batch";

        const monthName = currentDate.toLocaleString(
            "en-US",
            {
                month: "long",
            }
        );

        const fileName =
            `Attendance_${safeActivityName}_${safeBatchName}_${monthName}_${year}.xlsx`;

        XLSX.writeFile(workbook, fileName);
    };

    return (
        <Stack spacing={2}>
            {/* Header */}
            <Paper sx={{ p: 2 }}>
                <Typography variant="h6">
                    Attendance Reports
                </Typography>
            </Paper>

            {/* Filters */}
            <Paper
                sx={{
                    p: 3,
                }}
            >
                <Grid container spacing={3} alignItems="stretch">

                    {/* Activity */}
                    <Grid item xs={12} md={4}>
                        <Box
                            sx={{
                                height: "100%",
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                p: 2,
                                backgroundColor: "background.paper",
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                fontWeight={600}
                                sx={{ mb: 1.5 }}
                            >
                                Select Activity
                            </Typography>

                            <AutoCompleteServerSide
                                label="Select Activity *"
                                name="activity"
                                id="activity"
                                fullWidth
                                fetchDataFunction={(params) =>
                                    props.getActivityList(params)
                                }
                                onChange={handleActivityChange}
                                defaultValue={selectedActivity}
                                apiParams={{
                                    active: true,
                                    batch_status: true,
                                    type: "enrollment",
                                }}
                            />
                        </Box>
                    </Grid>

                    {/* Batch */}
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                height: "100%",
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                p: 2,
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                fontWeight={600}
                                sx={{ mb: 1.5 }}
                            >
                                Select Batch
                            </Typography>

                            <Grid container spacing={1.5}>
                                {selectedActivity?.batch_data
                                    ?.filter(
                                        (batch) =>
                                            batch?.type === "enrollment" &&
                                            batch?.status === true
                                    )
                                    .map((batch) => {
                                        const isSelected =
                                            selectedBatch?._id === batch._id;

                                        return (
                                            <Grid
                                                item
                                                xs={12}
                                                sm={6}
                                                key={batch._id}
                                            >
                                                <Button
                                                    fullWidth
                                                    variant={
                                                        isSelected
                                                            ? "contained"
                                                            : "outlined"
                                                    }
                                                    onClick={() => {
                                                        setSelectedBatch(batch);
                                                        setMembers([]);
                                                    }}
                                                    sx={{
                                                        minHeight: 48,
                                                        justifyContent: "flex-start",
                                                        textAlign: "left",
                                                        borderRadius: 1.5,
                                                        px: 2,
                                                        fontWeight: 500,
                                                        textTransform: "none",
                                                    }}
                                                >
                                                    {batch.batch_name}
                                                </Button>
                                            </Grid>
                                        );
                                    })}

                                {!selectedActivity && (
                                    <Grid item xs={12}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Select an activity to view available batches.
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </Grid>

                    {/* Actions */}
                    <Grid item xs={12} md={2}>
                        <Stack
                            spacing={1.5}
                            sx={{
                                height: "100%",
                                justifyContent: "center",
                            }}
                        >
                            <Button
                                fullWidth
                                loading={loading}
                                disabled={!selectedActivity || !selectedBatch}
                                onClick={handleFindMembers}
                                sx={{
                                    minHeight: 48,
                                    borderRadius: 1.5,
                                    textTransform: "none",
                                    fontWeight: 600,
                                }}
                            >
                                Find
                            </Button>

                            <Button
                                fullWidth
                                variant="contained"
                                disabled={!members || members.length === 0}
                                onClick={handleGenerateExcel}
                                sx={{
                                    minHeight: 48,
                                    borderRadius: 1.5,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    backgroundColor: "#7B1FA2",
                                    "&:hover": {
                                        backgroundColor: "#6A1B9A",
                                    },
                                }}
                            >
                                Generate Excel Report
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* Temporary result */}
            <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Enrolled Students: {members.length}
                </Typography>

                <AttendenceReportsTable
                    data={members}
                    loading={loading}
                />
            </Paper>
        </Stack>
    );
};

export default AttendenceReportsContainer;